/* eslint-disable @typescript-eslint/no-explicit-any */
import { GraphQLQuery, GraphQLSubscription } from "@aws-amplify/api";
import { useAuthenticator } from "@aws-amplify/ui-react";
import {
  ActionIcon,
  Box,
  Breadcrumbs,
  Button,
  Center,
  Flex,
  Group,
  rem,
  Text,
  TextInput
} from "@mantine/core";
import { IconPlus, IconSearch, IconX } from "@tabler/icons-react";
import { generateClient } from "aws-amplify/api";
import {
  fetchUserAttributes,
  FetchUserAttributesOutput
} from "aws-amplify/auth";
import { getUrl } from "aws-amplify/storage";
import { useEffect, useRef, useState } from "react";
import {
  GetLocationQuery,
  ModelSortDirection,
  OnCreatePatientSubscription,
  OnCreatePatientSubscriptionVariables,
  OnDeletePatientSubscription,
  OnDeletePatientSubscriptionVariables,
  OnUpdatePatientSubscription,
  OnUpdatePatientSubscriptionVariables,
  Patient,
  PatientsByLIDAndUpdatedAtQuery,
  PatientsByLIDAndUpdatedAtQueryVariables
} from "../API.ts";
import BoxLoader from "../components/BoxLoader.tsx";
import AddNewPatientModal from "../components/Patient/AddNewPatientModal.tsx";
import PatientTable from "../components/Patient/PatientTable/PatientTable.tsx";
import { patientsByLIDAndUpdatedAtShallow } from "../graphql/patientsByLIDAndUpdatedAtShallow.ts";
import { getLocation } from "../graphql/queries.ts";
import useCreatePatient from "../hooks/useCreatePatient";
import {
  onCreatePatient,
  onDeletePatient,
  onUpdatePatient
} from "../graphql/subscriptions.ts";
import { NavLink, useLocation } from "react-router-dom";
import { trackEvent } from "@intercom/messenger-js-sdk";
const client = generateClient();

// interface Location {
//   id: string;
//   groupUsers?: string;
//   groupAdmins?: string;
// }

function Patients() {
  const locationState = useLocation();
  const state = locationState.state;

  // set states
  const [patients, setPatients] = useState<Patient[]>([]);
  const [refreshing, setRefreshing] = useState(true);
  // const [locationId, setLocationId] = useState("");
  const [location, setLocation] = useState({});
  const [search, setSearch] = useState("");
  // might be alright without lowercase
  const userSelector = (context: { user: any }) => [context.user];
  const { user } = useAuthenticator(userSelector);
  const { handleCreatePatient } = useCreatePatient();
  const [nextToken, setNextToken] = useState("");
  const queryLimit = 20;

  const [modalOpened, setModalOpened] = useState(false);
  const [shouldFetchData, setShouldFetchData] = useState(false);

  const [patientName, setPatientName] = useState("");
  const [patientClient, setPatientClient] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (state?.addPatient) {
      setModalOpened(true);
    }
  }, []);

  // const [message, setMessage] = useState("");
  const inputSearchRef = useRef<any>(null);
  useEffect(() => {
    fetchData();
  }, []);

  // Handle input change
  const onSearchInputChange = (event: any) => {
    let { value } = event.currentTarget;
    if (value) {
      value = value.trim().toLowerCase();
    }
    setSearch(value);
  };
  const fetchData = async () => {
    setRefreshing(true);
    try {
      const userInfo: FetchUserAttributesOutput = await fetchUserAttributes();
      if (userInfo["custom:location"] === undefined) {
        setRefreshing(false);
        return;
      }
      const locationData = await client.graphql<GraphQLQuery<GetLocationQuery>>(
        { query: getLocation, variables: { id: userInfo["custom:location"] } }
      );
      const locationItem = locationData.data.getLocation;

      setLocation(locationItem!);

      const patientData = await fetchPatientList(userInfo);
      const patientsResult = patientData.data?.patientsByLIDAndUpdatedAt;
      if (!patientsResult) {
        setRefreshing(false);
        return;
      }

      const patientList = await fetchPatientImages(patientData);

      const filteredPatientList = patientList.filter(
        (patient): patient is Patient => patient !== undefined
      );

      setPatients(filteredPatientList.sort(sortByUpdatedAt));
      setNextToken(patientsResult.nextToken || "");
      
      // Trigger patient migration to Supabase when viewing patient list
      if (filteredPatientList.length > 0) {
        console.log(`[PATIENT-MIGRATION] Found ${filteredPatientList.length} patients to migrate to Supabase`);
        
        // Migrate patients in background (don't await to avoid blocking UI)
        setTimeout(async () => {
          try {
            const { DatabaseMigrationAdapter } = await import("@/services/migration");
            const migrationAdapter = DatabaseMigrationAdapter.getInstance();
            
            // Batch migrate patients
            for (let i = 0; i < filteredPatientList.length; i++) {
              const patient = filteredPatientList[i];
              try {
                console.log(`[PATIENT-MIGRATION] Migrating patient ${i + 1}/${filteredPatientList.length}:`, patient.name);
                await migrationAdapter.handlePatientOperation('CREATE', patient as Record<string, unknown>, { data: { createPatient: patient } } as Record<string, unknown>);
                console.log(`[PATIENT-MIGRATION] Successfully migrated patient:`, patient.name);
              } catch (patientError) {
                console.warn(`[PATIENT-MIGRATION] Failed to migrate patient ${patient.name}:`, patientError);
              }
              
              // Small delay between patients to prevent overwhelming
              if (i < filteredPatientList.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 200));
              }
            }
            console.log(`[PATIENT-MIGRATION] Batch migration completed`);
          } catch (error) {
            console.warn('[PATIENT-MIGRATION] Migration adapter failed:', error);
          }
        }, 1000); // Start after 1 second
      }
      
      setRefreshing(false);
      const createSub = createSubscriptionForPatient(locationItem);
      const updateSub = updateSubscriptionForPatient(locationItem);
      const deleteSub = deleteSubscriptionForPatient(locationItem);
      return () => {
        createSub.unsubscribe();
        updateSub.unsubscribe();
        deleteSub.unsubscribe();
      };
    } catch (err) {
      console.warn(err);
    }
    setRefreshing(false);
  };
  const fetchPatientImages = async (patientData: any) => {
    return await Promise.all(
      patientData.data.patientsByLIDAndUpdatedAt!.items.map(
        async (patient: any) => {
          try {
            patient!.s3Image = (
              await getUrl({
                path: `public/${patient!.image}`,
                options: { validateObjectExistence: true }
              })
            ).url.toString();
            // patient.s3Image = image;
          } catch (err) {
            console.warn("error getting patient image", patient!.name, err);
            patient!.s3Image = (
              await getUrl({
                path: `public/pet-image-placeholder.png`,
                options: { validateObjectExistence: true }
              })
            ).url.toString();
            // patient.s3Image = s3Image;
          }
          return patient;
        }
      )
    );
  };
  const fetchPatientList = async (userInfo: any) => {
    const patientQueryInput: PatientsByLIDAndUpdatedAtQueryVariables = {
      lID: userInfo["custom:location"],
      limit: queryLimit,
      sortDirection: ModelSortDirection.DESC,
      filter: {
        _deleted: { ne: true }
      }
    };
    const patientData = await client.graphql<
      GraphQLQuery<PatientsByLIDAndUpdatedAtQuery>
    >({
      query: patientsByLIDAndUpdatedAtShallow,
      variables: patientQueryInput
    });
    return patientData;
  };
  const deleteSubscriptionForPatient = (locationItem: any) => {
    const varsDeletePatient: OnDeletePatientSubscriptionVariables = {
      filter: {
        lID: { eq: locationItem!.id }
      }
    };
    return client
      .graphql<GraphQLSubscription<OnDeletePatientSubscription>>({
        query: onDeletePatient,
        variables: varsDeletePatient
      })
      .subscribe({
        next: () => {
          fetchPatients();
        },
        error: (error) => console.warn(error)
      });
  };
  const createSubscriptionForPatient = (locationItem: any) => {
    const varsCreatePatient: OnCreatePatientSubscriptionVariables = {
      filter: {
        lID: { eq: locationItem!.id }
      }
    };
    return client
      .graphql<GraphQLSubscription<OnCreatePatientSubscription>>({
        query: onCreatePatient,
        variables: varsCreatePatient
      })
      .subscribe({
        next: async () => {
          fetchPatients();
        },
        error: (error) => console.warn(error)
      });
  };
  const updateSubscriptionForPatient = (locationItem: any) => {
    const varsUpdatePatient: OnUpdatePatientSubscriptionVariables = {
      filter: {
        lID: { eq: locationItem!.id }
      }
    };
    return client
      .graphql<GraphQLSubscription<OnUpdatePatientSubscription>>({
        query: onUpdatePatient,
        variables: varsUpdatePatient
      })
      .subscribe({
        next: async ({ data }) => {
          // data.onUpdatePatient.s3Image = await Storage.get(data.onUpdatePatient.image) // OLD
          // data.onUpdatePatient.s3Image = (await getUrl({ path: data.onUpdatePatient.image, options: { validateObjectExistence: true } })).url.toString();
          const patientImage = data.onUpdatePatient!.image;
          try {
            const image = (
              await getUrl({
                path: `public/${patientImage}`,
                options: { validateObjectExistence: true }
              })
            ).url.toString();
            data.onUpdatePatient!.s3Image = image;
          } catch (err) {
            console.warn(err);
            const image = (
              await getUrl({
                path: `public/pet-image-placeholder.png`,
                options: { validateObjectExistence: true }
              })
            ).url.toString();
            data.onUpdatePatient!.s3Image = image;
          }

          // setPatients(patients! => {
          //   const toUpdateIndex = patients.findIndex(item => item.id === data.onUpdatePatient!.id)
          //   if (toUpdateIndex === - 1) { // If the patient doesn't exist, treat it like an "add"
          //     return [...patients, data.onUpdatePatient].sort(sortByUpdatedAt)
          //   }
          //   return [...patients.slice(0, toUpdateIndex), data.onUpdatePatient, ...patients.slice(toUpdateIndex + 1)].sort(sortByUpdatedAt)
          // })
        },
        error: (error) => console.warn(error)
      });
  };
  const fetchPatients = async () => {
    setRefreshing(true);
    try {
      const userInfo: FetchUserAttributesOutput = await fetchUserAttributes();
      if (userInfo["custom:location"] === undefined) {
        setRefreshing(false);
        return;
      }
      const locationData = await client.graphql<GraphQLQuery<GetLocationQuery>>(
        { query: getLocation, variables: { id: userInfo["custom:location"] } }
      );
      const locationItem = locationData.data.getLocation;
      setLocation(locationItem!);
      const patientQueryInput: PatientsByLIDAndUpdatedAtQueryVariables = {
        lID: userInfo["custom:location"],
        limit: queryLimit,
        filter: {
          _deleted: { ne: true }
        },
        sortDirection: ModelSortDirection.DESC
      };

      const patientData: any = await client.graphql<
        GraphQLQuery<PatientsByLIDAndUpdatedAtQuery>
      >({
        query: patientsByLIDAndUpdatedAtShallow,
        variables: patientQueryInput
      });
      const isUseImage = localStorage.getItem("useImages");
      if (isUseImage == "true") {
        const patientList = await fetchPatientImages(patientData);
        setPatients(patientList!.sort(sortByUpdatedAt));
      } else {
        setPatients(
          patientData.data.patientsByLIDAndUpdatedAt?.items?.sort(
            sortByUpdatedAt
          )
        );
      }

      setNextToken(patientData.data.patientsByLIDAndUpdatedAt!.nextToken!);
    } catch (err) {
      console.warn(err);
    }
    setRefreshing(false);
  };

  const fetchMorePatients = async () => {
    if (nextToken === null || nextToken == "") return;
    setLoading(true);
    try {
      // list by location
      const userInfo: FetchUserAttributesOutput = await fetchUserAttributes();
      if (userInfo["custom:location"] === undefined) {
        setLoading(false);
        return;
      }
      const patientQueryInput: PatientsByLIDAndUpdatedAtQueryVariables = {
        lID: userInfo["custom:location"],
        limit: queryLimit,
        nextToken: nextToken,
        filter: {
          _deleted: { ne: true }
        },
        sortDirection: ModelSortDirection.DESC
      };
      const patientData: any = await client.graphql<
        GraphQLQuery<PatientsByLIDAndUpdatedAtQuery>
      >({
        query: patientsByLIDAndUpdatedAtShallow,
        variables: patientQueryInput
      });
      const isUseImage = localStorage.getItem("useImages");
      if (isUseImage == "true") {
        const patientList = await fetchPatientImages(patientData);
        setPatients(patients.concat(patientList).sort(sortByUpdatedAt));
      } else {
        setPatients(
          patients
            .concat(patientData.data.patientsByLIDAndUpdatedAt?.items)
            .sort(sortByUpdatedAt)
        );
      }

      setNextToken(patientData.data.patientsByLIDAndUpdatedAt!.nextToken!);
    } catch (err) {
      console.warn(err);
    }
    setLoading(false);
  };

  const searchPatients = async (query?: string) => {
    setLoading(true);
    try {
      query = query || search;
      // list by location
      const userInfo: FetchUserAttributesOutput = await fetchUserAttributes();
      const patientQueryInput: PatientsByLIDAndUpdatedAtQueryVariables = {
        lID: userInfo["custom:location"]!,
        limit: queryLimit,
        sortDirection: ModelSortDirection.DESC,
        filter: {
          or: [
            { name: { contains: query.toLowerCase() } },
            { client: { contains: query?.toLowerCase() } }
          ]
        }
      };

      const patientData: any = await client.graphql<
        GraphQLQuery<PatientsByLIDAndUpdatedAtQuery>
      >({
        query: patientsByLIDAndUpdatedAtShallow,
        variables: patientQueryInput
      });
      const isUseImage = localStorage.getItem("useImages");
      if (isUseImage == "true") {
        const patientList = await fetchPatientImages(patientData);
        setPatients(patientList.sort(sortByUpdatedAt));
      } else {
        setPatients(
          patientData.data.patientsByLIDAndUpdatedAt?.items?.sort(
            sortByUpdatedAt
          )
        );
      }
      setNextToken(patientData.data.patientsByLIDAndUpdatedAt!.nextToken!);
    } catch (err) {
      console.warn(err);
    }
    setLoading(false);
  };

  const searchMorePatients = async () => {
    // if the next token is null, theres no data left. or there was an error loading the initial data
    if (nextToken === null || nextToken == "") return;
    setLoading(true);
    try {
      // list by location
      const userInfo: FetchUserAttributesOutput = await fetchUserAttributes();
      const patientQueryInput: PatientsByLIDAndUpdatedAtQueryVariables = {
        lID: userInfo["custom:location"]!,
        limit: queryLimit,
        sortDirection: ModelSortDirection.DESC,
        filter: {
          or: [
            { name: { contains: search?.toLowerCase() } },
            { client: { contains: search?.toLowerCase() } }
          ]
        },
        nextToken: nextToken
      };
      const patientData: any = await client.graphql<
        GraphQLQuery<PatientsByLIDAndUpdatedAtQuery>
      >({
        query: patientsByLIDAndUpdatedAtShallow,
        variables: patientQueryInput
      });
      const isUseImage = localStorage.getItem("useImages");
      if (isUseImage == "true") {
        const patientList = await fetchPatientImages(patientData);
        setPatients(patients.concat(patientList).sort(sortByUpdatedAt));
      } else {
        setPatients(
          patients
            .concat(patientData.data.patientsByLIDAndUpdatedAt?.items)
            .sort(sortByUpdatedAt)
        );
      }

      setNextToken(patientData.data.patientsByLIDAndUpdatedAt!.nextToken!);
    } catch (err) {
      console.warn(err);
    }
    setLoading(false);
  };

  function sortByUpdatedAt(
    a: { updatedAt: string | number | Date },
    b: { updatedAt: string | number | Date }
  ) {
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  }

  const handleAddPatient = async (
    location: { groupUsers: any; groupAdmins: any } | any
  ) => {
    setLoading(true);
    setModalOpened(false);
    try {
      if (patientName === "" && patientClient === "") {
        // setMessage("Please provide both patient and client names");
        setLoading(false);
        return;
      }

      console.log("[PATIENT-CREATE] Starting patient creation with Supabase logging");
      console.log("Location:" + JSON.stringify(location));
      console.log("User:" + JSON.stringify(user));

      // Use the dual-write hook that includes Supabase logging
      const addPatientResult = await handleCreatePatient(patientName, patientClient);
      console.log("[PATIENT-CREATE] Patient created successfully:", addPatientResult);
      trackEvent('create_patient');
    } catch (err) {
      console.log("[PATIENT-CREATE] Error creating patient:", err);
    }
    handleCancel();
    setLoading(false);
  };

  const handleCancel = () => {
    setPatientName("");
    setPatientClient("");
  };

  const searchIcon = <IconSearch style={{ width: rem(16), height: rem(16) }} />;
  const onClearSearch = () => {
    if (inputSearchRef.current != null) {
      inputSearchRef.current.value = "";
    }
    setSearch("");
    if (shouldFetchData) {
      fetchPatients();
      setShouldFetchData(false);
    }
  };
  return (
    <Group
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex",
        width: "100%",
        maxHeight: "100%",
        padding: 15
      }}
    >
      {/* Header */}
      <Group
        className="header-patients"
        flex={1}
        align="center"
        w={"100%"}
        justify="space-between"
      >
        <Breadcrumbs separatorMargin={3} mt={0} separator="→">
          <NavLink to={""} className={"active"}>
            Patients
          </NavLink>
        </Breadcrumbs>
        <Group className="header-patients-action">
          <TextInput
            leftSection={searchIcon}
            style={{ minWidth: "320px" }}
            placeholder="Search patient name or client name"
            radius={20}
            ref={inputSearchRef}
            onChange={onSearchInputChange}
            rightSection={
              search && (
                <ActionIcon radius={"50%"} onClick={onClearSearch} size={"sm"}>
                  <IconX size={16} />
                </ActionIcon>
              )
            }
          ></TextInput>
          {search && (
            <Button
              radius={20}
              onClick={() => {
                setShouldFetchData(true);
                searchPatients();
              }}
              fw={400}
              variant="default"
            >
              <IconPlus width={16} />
              <Text fw={400} fz={14}>
                Search all patients
              </Text>
            </Button>
          )}
          <Button radius={20} onClick={() => setModalOpened(true)} fw={400}>
            <IconPlus width={16} />
            <Text fw={400} fz={14}>
              Add new patient
            </Text>
          </Button>
        </Group>
      </Group>
      <Flex
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          width: "100%",
          minHeight: "calc(100vh - 300px)"
        }}
      >
        {refreshing ? (
          <BoxLoader />
        ) : patients.length == 0 ? (
          <Center pos={"relative"}>
            <Flex direction={"column"} justify={"center"} ta={"center"}>
              <p>No patients found</p>
              <p>Please add some patients</p>
              <Button radius={20} onClick={() => setModalOpened(true)} fw={400}>
                <IconPlus width={16} />
                <Text fw={400} fz={14}>
                  Add new patient
                </Text>
              </Button>
              <Box mt={16}>
                <Box>
                  <Text fw={400}>Tip:</Text> You can search for patients by name
                  or client name.
                </Box>
              </Box>
            </Flex>
          </Center>
        ) : (
          <Group
            style={{
              width: "100%",
              display: "flex",
              flex: "1",
              alignItems: "center",
              position: "relative",
              justifyContent: "center"
            }}
          >
            <PatientTable
              data={patients}
              isLoading={loading}
              search={search}
              loadMorePatients={search ? searchMorePatients : fetchMorePatients}
              fetchPatients={search ? searchPatients : fetchPatients}
            />
            {loading && <BoxLoader />}
          </Group>
        )}
      </Flex>
      <AddNewPatientModal
        location={location}
        handleAddPatient={handleAddPatient}
        modalOpened={modalOpened}
        setModalOpened={setModalOpened}
        patientName={patientName}
        setPatientName={setPatientName}
        patientClient={patientClient}
        setPatientClient={setPatientClient}
      />
    </Group>
  );
}

export default Patients;
